import { useEffect, useMemo, useState } from 'react';
import { Header } from '@/components';
import { startContactSocials, pollContactSocialsResults, type ContactSocialResult } from '@/services/contactSocials';
import { getCredits } from '@/services/credits';
import { enqueueScrapeJob } from '@/services/scrapeQueue';
import { loadAllContacts } from '@/services/contacts';
import { useAppStore } from '@/stores/appStore';

const SOCIAL_PLATFORMS = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'x', label: 'X' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'podcast', label: 'Podcast' },
];

type Contact = {
  searchResultId?: string;
  fullName?: string;
  Name?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  Company?: string;
  ContactLocation?: string;
  CompanyLocation?: string;
  email?: string;
  Email?: string;
  email1?: string;
  email2?: string;
  email3?: string;
  personalEmail?: string;
  PersonalEmail?: string;
  personalEmail1?: string;
};

export function ContactSocials() {
  const { userEmail, setCredits } = useAppStore();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactQuery, setContactQuery] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [location, setLocation] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [platforms, setPlatforms] = useState<Record<string, boolean>>(
    SOCIAL_PLATFORMS.reduce((acc, p) => ({ ...acc, [p.key]: true }), {})
  );
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [results, setResults] = useState<ContactSocialResult[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    if (!userEmail) return;
    const controller = new AbortController();
    const loadContacts = async () => {
      try {
        const all = await loadAllContacts(userEmail, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setContacts(all || []);
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        setContacts([]);
      }
    };
    loadContacts();
    return () => controller.abort();
  }, [userEmail]);

  const filteredContacts = useMemo(() => {
    if (!contactQuery.trim()) return [];
    const q = contactQuery.toLowerCase();
    return contacts.filter((c) => {
      const name = (c.fullName || c.Name || '').toLowerCase();
      const company = (c.company || c.Company || '').toLowerCase();
      const email = (c.email || c.Email || c.email1 || '').toLowerCase();
      return name.includes(q) || company.includes(q) || email.includes(q);
    }).slice(0, 8);
  }, [contacts, contactQuery]);

  const applyContact = (contact: Contact) => {
    const name = contact.fullName || contact.Name || [contact.firstName, contact.lastName].filter(Boolean).join(' ');
    const company = contact.company || contact.Company || '';
    const loc = contact.ContactLocation || contact.CompanyLocation || '';
    const personal =
      contact.personalEmail ||
      contact.PersonalEmail ||
      contact.personalEmail1 ||
      '';
    const business =
      contact.email ||
      contact.Email ||
      contact.email1 ||
      contact.email2 ||
      contact.email3 ||
      '';

    setFullName(name || '');
    setCompanyName(company || '');
    setLocation(loc || '');
    setPersonalEmail(personal || '');
    setBusinessEmail(business || '');
    setContactQuery(name || '');
  };

  const selectedPlatforms = useMemo(
    () => SOCIAL_PLATFORMS.filter((p) => platforms[p.key]).map((p) => p.key),
    [platforms]
  );

  const filteredResults = useMemo(() => {
    if (!searchTerm) return results;
    const q = searchTerm.toLowerCase();
    return results.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [results, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredResults.length / perPage));
  const pagedResults = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredResults.slice(start, start + perPage);
  }, [filteredResults, currentPage, perPage]);

  const togglePlatform = (key: string) => {
    setPlatforms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSearch = () => {
    if (!fullName.trim()) {
      setStatus('Contact name is required.');
      return;
    }
    if (!userEmail) {
      setStatus('Please log in to run this search.');
      return;
    }
    setLoading(true);
    setStatus(null);
    setStatus('Queued. Running in background...');

    const payload = {
      account_id: userEmail,
      name: fullName.trim(),
      company_name: companyName.trim() || undefined,
      location: location.trim() || undefined,
      personal_email: personalEmail.trim() || undefined,
      business_email: businessEmail.trim() || undefined,
      platforms: selectedPlatforms,
      max_results: 50,
    };

    enqueueScrapeJob(
      {
        source: 'contact_socials',
        companyName: fullName.trim(),
        label: `Contact Socials - ${fullName.trim()}`,
      },
      async (update) => {
        try {
          update(10, 'Preparing search…');
          const creds = await getCredits(userEmail);
          setCredits(creds);

          update(35, 'Starting search…');
          const start = await startContactSocials(payload);
          if (!start.success || !start.run_id) throw new Error(start.error || 'Search failed');

          update(55, 'Searching sources…');
          const res = await pollContactSocialsResults(start.run_id, (progress, message) => {
            update(progress, message);
          });

          update(90, 'Processing results…');
          setResults(res.results ?? []);
          setCurrentPage(1);
          setStatus(`Found ${res.results?.length ?? 0} results.`);
        } catch (err: any) {
          setStatus(err?.message || 'Search failed');
          throw err;
        } finally {
          setLoading(false);
        }
      }
    );
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Contact Socials Intelligence</h2>
            <p className="text-sm text-gray-400 mt-1">Discover profile URLs and usernames across social platforms.</p>
          </div>
          <div className="text-sm text-gray-400">
            Credits: <span className="text-white font-bold">2</span> per search
          </div>
        </div>
      </div>

      <div className="bg-card-dark border-b border-gray-800 px-6 py-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="rounded-lg border border-gray-800 bg-gray-900/30 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500">My Lists Contacts</div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Search Contact</label>
                <input
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                  placeholder="Type a name, company, or email"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                />
              </div>
              <div className="text-xs text-gray-400 flex items-end">
                {contacts.length > 0 ? `${contacts.length} contacts loaded` : 'No contacts loaded'}
              </div>
            </div>
            {filteredContacts.length > 0 && (
              <div className="mt-3 space-y-2">
                {filteredContacts.map((c, idx) => {
                  const name = c.fullName || c.Name || [c.firstName, c.lastName].filter(Boolean).join(' ');
                  const company = c.company || c.Company || '';
                  const email = c.email || c.Email || c.email1 || c.personalEmail || '';
                  return (
                    <button
                      key={`${c.searchResultId ?? idx}`}
                      type="button"
                      onClick={() => applyContact(c)}
                      className="w-full text-left px-3 py-2 rounded-lg border border-gray-800 bg-gray-800/40 hover:bg-gray-800 text-sm"
                    >
                      <div className="text-white">{name || 'Unknown Name'}</div>
                      <div className="text-xs text-gray-400">{company}{company && email ? ' · ' : ''}{email}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Contact Name *</label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Full Name"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Company Name</label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Company (optional)"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Location</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City/State/Country"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Personal Email</label>
              <input
                value={personalEmail}
                onChange={(e) => setPersonalEmail(e.target.value)}
                placeholder="name@email.com"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Business Email</label>
              <input
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
                placeholder="name@company.com"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-400">Social Platforms:</span>
            {SOCIAL_PLATFORMS.map((p) => (
              <button
                key={p.key}
                onClick={() => togglePlatform(p.key)}
                className={`px-3 py-1 rounded-full text-xs border ${
                  platforms[p.key]
                    ? 'bg-red-600 border-red-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400'
                }`}
                type="button"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold text-sm disabled:opacity-50"
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
            <span className="text-xs text-gray-400">Runs in background. Track progress in the status bar.</span>
            {status && <span className="text-xs text-gray-400">{status}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h3 className="text-lg font-semibold">Results</h3>
            <div className="flex gap-2">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filter results..."
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm"
              />
              <select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                className="px-2 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}/page</option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-800">
                  <th className="py-2 pr-4">Platform</th>
                  <th className="py-2 pr-4">Profile</th>
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Snippet</th>
                  <th className="py-2 pr-4">Confidence</th>
                  <th className="py-2 pr-4">Details</th>
                </tr>
              </thead>
              <tbody>
                {pagedResults.map((r, idx) => (
                  <tr key={idx} className="border-b border-gray-900 align-top">
                    <td className="py-2 pr-4 text-gray-300">{r.platform}</td>
                    <td className="py-2 pr-4 text-blue-400">
                      {r.profile_url ? (
                        <a href={r.profile_url} target="_blank" rel="noreferrer" className="hover:underline">
                          {r.profile_url}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="py-2 pr-4 text-gray-300">{r.title ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">{r.snippet ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-300">{r.confidence ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-300">
                      <details className="text-xs">
                        <summary className="cursor-pointer text-blue-400">View</summary>
                        <pre className="mt-2 p-2 bg-gray-900 border border-gray-800 rounded text-gray-300 max-h-64 overflow-auto">{JSON.stringify(r, null, 2)}</pre>
                      </details>
                    </td>
                  </tr>
                ))}
                {pagedResults.length === 0 && (
                  <tr>
                    <td className="py-4 text-gray-500" colSpan={6}>
                      No results yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Page {currentPage} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 bg-gray-800 rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 bg-gray-800 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
